import _ from 'underscore';
import ko from 'knockout';

function isLastLevel(pairs) {
  return _.isEmpty(pairs) || _.isString(pairs[0]);
}

function isValidPath(path, pairs) {
  if (_.isEmpty(path)) {
    if (isLastLevel(pairs)) {
      return true;
    } else {
      return false;
    }
  }

  let result = _.find(pairs, (i) => _.first(i) === _.first(path));

  if (_.size(pairs) === 1 && _.size(path) === 1) {
    if (_.indexOf(pairs[0], path[0]) > -1) {
      result = pairs[0];
    }
  }

  if (_.isEmpty(result)) {
    return false;
  }

  return isValidPath(_.rest(path), _.rest(result));
}

function getDefaultPath(pairs) {
  if (_.isString(pairs[0])) {
    return [pairs[0]];
  }

  const [key, val] = pairs[0];
  const paths = getDefaultPath(val);

  return [key, ...paths];
}

class CxSelector {
  constructor(path, selectsCss, i18n, pairs, caption = null, selectsLabel = null) {
    this.css = selectsCss[0] || '';
    this.label = (selectsLabel && selectsLabel[0]) || undefined;
    this.pairs = pairs;
    this.isLastLevel = isLastLevel(pairs);
    let options;

    if (this.isLastLevel) {
      const defaultValue = caption ? null : pairs[0];

      this.value = ko.observable(path[0] || defaultValue);
      options = pairs;

      this.caption = caption;
    } else {
      this.value = ko.observable(path[0] || pairs[0][0]);

      options = _.map(pairs, _.first);

      this.caption = null;

      this.subCxSelector = ko.computed(() => {
        const v = this.value();

        return !_.isEmpty(v) && new CxSelector(_.rest(path), _.rest(selectsCss), i18n, _.find(pairs, (i) => _.first(i) === v)[1], caption, _.rest(selectsLabel));
      });
    }

    this.opts = _.map(options, (opt) => {
      return {
        optionsValue: opt,
        optionsText: i18n.get(opt),
      };
    });
  }

  toArray() {
    const sub = ko.unwrap(this.subCxSelector);

    return sub ? [this, ...sub.toArray()] : [this];
  }
}

export default class ViewModel {
  constructor({
    value = '',
    selectsCss = [],
    selectsLabel = [],
    selectOptions = [],
    caption = null,
    i18n = { get: _.identity },
  } = {}) {
    this.selectsCss = selectsCss;
    this.selectsLabel = selectsLabel;
    this.i18n = i18n;
    this.selectOptions = selectOptions;
    this.caption = caption;
    this.hasEdited = false;
    this.isMultiMatch = false;
    this.matchedPaths = ViewModel.findValuePaths(value, selectOptions);

    if (_.size(this.matchedPaths) > 1) {
      this.isMultiMatch = true;
    }
    const initialPath = this.matchedPaths[0] || [];
    const defaultPath = _.isEmpty(initialPath) ? getDefaultPath(selectOptions) : initialPath;

    this.cxSelectorHead = ko.observable(new CxSelector(initialPath, selectsCss, i18n, selectOptions, caption, selectsLabel));
    this.cxSelectors = ko.computed(() => {
      return this.cxSelectorHead().toArray();
    });
    this.tempPath = ko.computed(() => {
      return _.map(this.cxSelectors(), (cxSelector) => {
        return cxSelector.value();
      });
    });
    this.path = ko.computed(() => {
      if (isValidPath(this.tempPath(), selectOptions)) {
        return this.tempPath();
      }
      return defaultPath;
    });
    this.path.subscribe(() => {
      this.hasEdited = true;
    });
  }

  updatePath(newPath) {
    if (!this.hasEdited &&
      this.isMultiMatch &&
      _.some(this.matchedPaths, (path) => _.isEqual(path, newPath))
    ) {
      this.cxSelectorHead(new CxSelector(newPath, this.selectsCss, this.i18n, this.selectOptions, this.caption, this.selectsLabel));
    }
  }

  static findValuePaths(value, pairs) {
    const ret = [];

    if (_.isEmpty(value)) {
      return ret;
    }

    if (isLastLevel(pairs)) {
      return _.contains(pairs, value) ? [[value]] : [];
    }

    // for .. of .. from babeljs needs Symbol
    for (let index in pairs) {
      const [key, val] = pairs[index];

      let results = ViewModel.findValuePaths(value, val);

      if (!_.isEmpty(results)) {
        _.each(results, (result) => ret.push([key, ...result]));
      }
    }

    return ret;
  }
}
